import { useEffect, useState } from 'react';
import { Input } from '@/components/ui/input';

/**
 * Plain-text input for monetary amounts. Uses `inputMode="decimal"` so
 * mobile keyboards show a numeric keypad and desktop browsers don't render
 * the +/- spinners that come with `type="number"`. Internal string state
 * lets the user type partial values ("5.", ".25") without losing focus or
 * input mid-edit; we parse on every change and emit the numeric value, but
 * keep the raw string in view until they blur.
 *
 * Empty input is treated as 0. NaN inputs are also treated as 0 — the
 * parent stays in a valid state but the visible field shows the user's
 * typed string until blur normalises it.
 */
export function CurrencyInput({
  value,
  onChange,
  disabled,
  id,
  placeholder = '0.00',
  'aria-label': ariaLabel,
  className,
}: {
  value: number;
  onChange: (next: number) => void;
  disabled?: boolean;
  id?: string;
  placeholder?: string;
  'aria-label'?: string;
  className?: string;
}) {
  const [text, setText] = useState(() => formatForEditing(value));

  // Re-sync from the parent when `value` changes externally (e.g. parent
  // resets a draft). Skip when the parent value already matches what we'd
  // produce from our current text, so user typing doesn't get clobbered.
  useEffect(() => {
    const parsed = parse(text);
    if (parsed !== value) setText(formatForEditing(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleChange = (raw: string) => {
    setText(raw);
    onChange(parse(raw));
  };

  const handleBlur = () => {
    // Normalise visible value on blur — "5." → "5", "" → "0".
    setText(formatForEditing(parse(text)));
  };

  return (
    <Input
      id={id}
      type="text"
      inputMode="decimal"
      value={text}
      onChange={(e) => handleChange(e.target.value)}
      onBlur={handleBlur}
      disabled={disabled}
      placeholder={placeholder}
      aria-label={ariaLabel}
      className={className}
    />
  );
}

// Strict numeric parse — rejects locale separators (",", "_"), scientific
// notation, hex, and any non-decimal noise instead of silently coercing via
// `Number()` which would turn '1,000' into 0 and '1e9' into 1,000,000,000.
// Thousands-separator commas are stripped first so a user typing '1,000'
// gets 1000, not 0.
function parse(raw: string): number {
  const trimmed = raw.trim().replace(/,/g, '');
  if (trimmed === '' || trimmed === '-' || trimmed === '.') return 0;
  if (!/^-?\d*\.?\d+$/.test(trimmed)) return 0;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : 0;
}

function formatForEditing(value: number): string {
  if (value === 0) return '';
  // Keep the user-facing string close to what they'd type — no thousands
  // separators, drop trailing .00 when the value is an integer.
  return Number.isInteger(value) ? String(value) : String(value);
}
